import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';

function EmailShellEditorDialogs({ context }) {
    const {
        draftEmailShell,
        editingEmailShell,
        emailShellImageFields,
        emailShellPreviewOpen,
        emailShellSocialFields,
        emailShellTextFields,
        isHttpUrl,
        normalizeArray,
        saveEmailShellDraft,
        savingEmailShellDraft,
        setEditingEmailShell,
        setEmailShellPreviewOpen,
        updateEmailShellSocialValue,
        updateEmailShellValue,
        validateEmailShellEditor
    } = context;

    const renderEmailShellEditDialog = () => {
        if (!editingEmailShell) {
            return null;
        }

        const localErrors = validateEmailShellEditor(
            editingEmailShell,
            emailShellImageFields,
            emailShellSocialFields
        );

        return (
            <Dialog open onClose={() => setEditingEmailShell(null)} fullWidth maxWidth="lg">
                <DialogTitle>Email Shell Draft Edit</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} sx={{ pt: 1 }}>
                        <Alert severity="warning" variant="outlined">
                            Draft editing only. Publishing updates the active Bundle Builder email preview shell and does not send emails or modify saved templates.
                        </Alert>
                        {localErrors.length > 0 && (
                            <Alert severity="error" variant="outlined">
                                {localErrors.slice(0, 3).join(' ')}
                                {localErrors.length > 3 ? ` ${localErrors.length - 3} more issue(s).` : ''}
                            </Alert>
                        )}
                        <Box>
                            <Typography variant="h6" gutterBottom>Store, Intro, Footer Text</Typography>
                            <Grid container spacing={2}>
                                {emailShellTextFields.map((field) => (
                                    <Grid item xs={12} md={field.multiline ? 12 : 6} key={field.key}>
                                        <TextField
                                            label={field.label}
                                            value={editingEmailShell[field.key] || ''}
                                            onChange={(event) => updateEmailShellValue(field.key, event.target.value)}
                                            fullWidth
                                            required={Boolean(field.required)}
                                            multiline={Boolean(field.multiline)}
                                            minRows={field.rows || (field.multiline ? 2 : undefined)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                        <Box>
                            <Typography variant="h6" gutterBottom>Images and Links</Typography>
                            <Grid container spacing={2}>
                                {emailShellImageFields.map((field) => (
                                    <Grid item xs={12} md={6} key={field.key}>
                                        <TextField
                                            label={field.label}
                                            value={editingEmailShell[field.key] || ''}
                                            onChange={(event) => updateEmailShellValue(field.key, event.target.value)}
                                            fullWidth
                                            error={Boolean(editingEmailShell[field.key]) && !isHttpUrl(editingEmailShell[field.key])}
                                            helperText="Use a full http(s) URL."
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                        <Box>
                            <Typography variant="h6" gutterBottom>Social / Footer Icons</Typography>
                            <Grid container spacing={2}>
                                {emailShellSocialFields.map((field) => (
                                    <Grid item xs={12} md={4} key={field.key}>
                                        <TextField
                                            label={field.label}
                                            value={editingEmailShell.socialIconUrls?.[field.key] || ''}
                                            onChange={(event) => updateEmailShellSocialValue(field.key, event.target.value)}
                                            fullWidth
                                            error={Boolean(editingEmailShell.socialIconUrls?.[field.key]) && !isHttpUrl(editingEmailShell.socialIconUrls?.[field.key])}
                                            helperText="Use a full http(s) URL."
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Stack spacing={1}>
                                <Typography variant="subtitle1" fontWeight={700}>Placeholder Lock</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    These placeholders stay available in the approved shell and are not editable in this phase.
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    {normalizeArray(editingEmailShell.requiredPlaceholders).map((placeholder) => (
                                        <Chip key={placeholder} label={placeholder} size="small" variant="outlined" />
                                    ))}
                                </Stack>
                            </Stack>
                        </Paper>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingEmailShell(null)} disabled={savingEmailShellDraft}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={saveEmailShellDraft} disabled={savingEmailShellDraft || localErrors.length > 0}>
                        {savingEmailShellDraft ? 'Saving...' : 'Save Draft'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
    const renderEmailShellPreviewDialog = () => {
        if (!emailShellPreviewOpen) {
            return null;
        }

        const shell = draftEmailShell || {};

        return (
            <Dialog open onClose={() => setEmailShellPreviewOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Email Shell Preview</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Alert severity="info" variant="outlined">
                            Previewing shell settings only. Bundle Builder still generates the Home Services quote table from selected Internet, TV, and Home Phone products.
                        </Alert>
                        <Paper variant="outlined" sx={{ p: 2, maxWidth: 600, mx: 'auto', width: '100%' }}>
                            <Stack spacing={2} alignItems="center">
                                {shell.headerImageUrl && (
                                    <Box component="img" src={shell.headerImageUrl} alt="Header preview" sx={{ maxWidth: '100%', width: 320 }} />
                                )}
                                <Typography variant="h4" color="primary" textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
                                    {shell.introHeadline}
                                </Typography>
                                {shell.rabbitHeroImageUrl && (
                                    <Box component="img" src={shell.rabbitHeroImageUrl} alt="Hero preview" sx={{ maxWidth: '100%', width: 360 }} />
                                )}
                                <Typography variant="body1" textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
                                    {shell.introSubheadline}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    From {shell.storeName} | {[shell.storeAddressLine1, shell.storeAddressLine2].filter(Boolean).join(', ')}
                                </Typography>
                                <Divider flexItem />
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {shell.customerIntroText}
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, width: '100%', bgcolor: 'grey.50' }}>
                                    <Typography variant="subtitle2" color="primary">Dynamic quote table appears here</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Product rows come from Bundle Builder selections.
                                    </Typography>
                                </Paper>
                                <Typography variant="h6" color="primary" textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
                                    {shell.agentContactHeadline}
                                </Typography>
                                <Typography variant="body2" textAlign="center">
                                    Agent Name: {'{{agent_name}}'} | Phone Number: {'{{agent_phone}}'} | Store: {shell.storePhone}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" textAlign="center">
                                    Footer/legal blocks remain part of the copied customer email HTML.
                                </Typography>
                            </Stack>
                        </Paper>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailShellPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <>
            {renderEmailShellEditDialog()}
            {renderEmailShellPreviewDialog()}
        </>
    );
}

export default EmailShellEditorDialogs;
